<script setup lang="ts">
import { useFocusTrap } from '@/composables/useFocusTrap';
import { computed, onBeforeUnmount, onMounted, ref, useId, type Component } from 'vue';
import Button from './Button.vue';

export interface ConfirmDialogProps {
    title: string;
    // Plain text: names in it are shown as typed, never parsed as markup.
    message?: string;
    // For a translation with markup (<strong>). Rendered as HTML, so build it
    // with escapeHtmlParams() from @/utils/html; wins over `message`.
    messageHtml?: string;
    confirmText?: string;
    cancelText?: string;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    variant?: 'danger' | 'warning' | 'primary' | 'success';
    customIcon?: Component;
    // Opt-in stacking bump for the rare case of a confirm dialog opened from
    // within another modal (which already sits at z-99999).
    elevated?: boolean;
    // While the confirmed action runs: spinner on confirm, and no way to cancel or confirm twice.
    isLoading?: boolean;
    // Defaults to danger for danger dialogs, primary otherwise.
    confirmVariant?: 'primary' | 'danger';
    // For a confirm that needs input first, e.g. a required reason.
    confirmDisabled?: boolean;
    // Test hook on the confirm button, since attrs land on the dialog root.
    confirmDataCy?: string;
}

const props = withDefaults(defineProps<ConfirmDialogProps>(), {
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    size: 'xl',
    variant: 'warning',
    customIcon: undefined,
    elevated: false,
    isLoading: false,
    confirmVariant: undefined,
    confirmDisabled: false,
    confirmDataCy: undefined,
});

const emit = defineEmits<{
    confirm: [];
    cancel: [];
}>();

const dialogRef = ref<HTMLElement | null>(null);
const titleId = useId();
const active = ref(true);
const requestCancel = () => {
    if (!props.isLoading) emit('cancel');
};

const resolvedConfirmVariant = computed(
    () => props.confirmVariant ?? (props.variant === 'danger' ? 'danger' : 'primary'),
);

useFocusTrap(dialogRef, { active, onEscape: requestCancel });

onMounted(() => {
    document.body.style.overflow = 'hidden';
});

onBeforeUnmount(() => {
    document.body.style.overflow = '';
});

const iconColor = computed(() => {
    switch (props.variant) {
        case 'danger':
            return 'text-error-500 fill-error-100 dark:fill-transparent';
        case 'warning':
            return 'text-warning-500 fill-warning-100 dark:fill-transparent';
        case 'success':
            // fill-success-100 (a light tint), not fill-success-500 — that solid fill
            // made the whole icon green instead of a tinted outline like the rest.
            return 'text-success-500 fill-success-100 dark:fill-transparent';
        case 'primary':
            return 'text-brand-500 fill-brand-100 dark:fill-transparent';
        default:
            return 'text-warning-500';
    }
});

// The badge circle follows the variant too; it was hardcoded yellow, so a
// success dialog showed a green icon inside a yellow circle.
const circleColor = computed(() => {
    switch (props.variant) {
        case 'danger':
            return 'bg-error-100 dark:bg-error-500/15';
        case 'success':
            return 'bg-success-100 dark:bg-success-500/15';
        case 'primary':
            return 'bg-brand-100 dark:bg-brand-900/30';
        case 'warning':
        default:
            return 'bg-warning-100 dark:bg-warning-500/15';
    }
});

const sizeClasses = computed(() => {
    switch (props.size) {
        case 'sm':
            return 'max-w-sm';
        case 'md':
            return 'max-w-md';
        case 'lg':
            return 'max-w-lg';
        case 'xl':
            return 'max-w-xl';
        default:
            return 'max-w-lg';
    }
});
</script>

<template>
    <div class="modal fixed inset-0 overflow-y-auto" :class="elevated ? 'z-[100000]' : 'z-99999'">
        <div
            class="fixed inset-0 h-full w-full bg-gray-600/50 backdrop-blur-[10px]"
            aria-hidden="true"
            @click="requestCancel"
        ></div>

        <!-- A wrapper at least as tall as the viewport. Centring on the
             scroll container itself leaves the top of a taller dialog
             out of reach, because the overflow above the centre point
             cannot be scrolled to. -->
        <div class="flex min-h-full items-center justify-center p-4">
            <div
                ref="dialogRef"
                class="relative z-10 w-full rounded-2xl bg-white p-6 shadow-theme-xl dark:bg-gray-dark dark:ring-1 dark:ring-white/10"
                :class="sizeClasses"
                role="dialog"
                aria-modal="true"
                :aria-labelledby="titleId"
            >
                <div class="mb-4 flex items-start">
                    <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full" :class="circleColor">
                        <component v-if="customIcon" :is="customIcon" class="h-6 w-6" :class="iconColor" />
                        <svg
                            v-else
                            class="h-6 w-6"
                            :class="iconColor"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                        >
                            <!-- success confirms a positive action (e.g. approving a quote), so it
                                 gets a check rather than the warning triangle used by the rest. -->
                            <path
                                v-if="variant === 'success'"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                            />
                            <path
                                v-else
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                            />
                        </svg>
                    </div>
                    <div class="ml-4 flex-1 text-left">
                        <h3 :id="titleId" class="text-lg font-semibold text-gray-900 dark:text-white">
                            {{ title }}
                        </h3>
                        <div class="mt-2 text-sm text-gray-600 dark:text-gray-300">
                            <slot name="message">
                                <!-- eslint-disable-next-line vue/no-v-html -- callers escape every value with escapeHtmlParams() -->
                                <p v-if="messageHtml" v-html="messageHtml" />
                                <p v-if="message && !messageHtml">{{ message }}</p>
                            </slot>
                        </div>
                    </div>
                </div>

                <!-- Below sm the pair stacks full width instead of being squeezed into
                     a shared row, where a long confirm label (e.g. the consumer
                     payment-obligation button) shrinks and wraps. flex-col-reverse puts
                     the primary on top while leaving cancel first in the DOM, so tab
                     order is unchanged. From sm up this is the original row.
                     sm:items-center, not the default stretch: a label that still wraps
                     would otherwise drag the cancel button to the same height. -->
                <div class="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        :disabled="isLoading"
                        @click="emit('cancel')"
                        size="sm"
                        :stop-propagation="true"
                        class="w-full sm:w-auto"
                    >
                        {{ cancelText }}
                    </Button>
                    <Button
                        type="button"
                        :variant="resolvedConfirmVariant"
                        :loading="isLoading"
                        :disabled="confirmDisabled"
                        :data-cy="confirmDataCy"
                        @click="emit('confirm')"
                        size="sm"
                        :stop-propagation="true"
                        class="w-full sm:w-auto"
                    >
                        {{ confirmText }}
                    </Button>
                </div>
            </div>
        </div>
    </div>
</template>
